import { useState } from 'react';
import { Modal } from './Modal';
import { Avatar } from './Avatar';
import { UiNode } from '../lib/UiNode';
import { IconCopy, IconCheck, IconPlus, IconTrash } from './Icon';
import { api } from '../api';
import { useRouter } from '../router';
import type { BoardDetail } from '../../shared/types';
import type { UiNodeName } from '../lib/ui-registry';

export function ShareSheet({
  open,
  onClose,
  board,
  me,
  onBoardChange,
}: {
  open: boolean;
  onClose: () => void;
  board: BoardDetail;
  me: import('../../shared/types').User;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<'board' | 'deck' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isOwner = board.role === 'owner';
  const boardUrl = `${window.location.origin}/boards/${board.id}`;
  const deckUrl = board.deckShareToken
    ? `${window.location.origin}/d/${board.deckShareToken}`
    : null;

  async function copy(url: string, which: 'board' | 'deck') {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // Fallback for older browsers / insecure contexts.
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    }
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.includes('@') || busy) return;
    setBusy(true);
    try {
      await api.invite(board.id, email.trim().toLowerCase());
      setEmail('');
      const next = await api.board(board.id);
      onBoardChange(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Share ${board.name}`} maxWidth={520}>
      <div className="flex flex-col gap-5">
        {/* TEAM FIRST — the most-used action in this modal is inviting
            someone or chasing a pending invite. Putting the link rows
            above the Team list buried the actual people-management. */}
        <Section title="Who's in" subtitle={`${board.members.length} member${board.members.length === 1 ? '' : 's'}${board.pendingInvites.length ? ` · ${board.pendingInvites.length} pending` : ''}`}>
          <ul className="flex flex-col gap-1.5">
            {board.members.map((m) => (
              <MemberRow
                key={m.id}
                board={board}
                member={m}
                isOwner={isOwner}
                isMe={m.id === me.id}
                onBoardChange={onBoardChange}
                onClose={onClose}
              />
            ))}
            {board.pendingInvites.map((i) => (
              <PendingInviteRow key={i.email} boardId={board.id} email={i.email} isOwner={isOwner} />
            ))}
          </ul>

          {isOwner && (
            <form
              className="mt-3 flex flex-col gap-2 rounded-xl border border-ink-rim bg-ink-3 p-3 sm:flex-row"
              onSubmit={invite}
            >
              <UiNode
                as="input"
                name="share-sheet-invite-input"
                entity="invitation"
                action="compose"
                className="input flex-1"
                type="email"
                placeholder="Invite by email — name@yourcompany.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              />
              <UiNode
                as="button"
                name="share-sheet-invite-submit"
                entity="invitation"
                action="send"
                state={busy ? 'busy' : 'idle'}
                className="btn btn-primary inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                type="submit"
                disabled={busy || !email.includes('@')}
              >
                {!busy && <IconPlus size={14} />}
                {busy ? 'Inviting…' : 'Send invite'}
              </UiNode>
            </form>
          )}

          {error && <p className="mt-2 text-xs text-rust">{error}</p>}
        </Section>

        <Section
          title="Share link"
          subtitle="Anyone signed in who you've added can open the board"
        >
          <LinkRow
            url={boardUrl}
            copied={copied === 'board'}
            onCopy={() => copy(boardUrl, 'board')}
            name="share-board-link"
          />
        </Section>

        {deckUrl && (
          <Section
            title="Public deck"
            subtitle="A read-only slide deck of the plan. No sign-in needed."
          >
            <LinkRow
              url={deckUrl}
              copied={copied === 'deck'}
              onCopy={() => copy(deckUrl, 'deck')}
              name="share-deck-link"
            />
          </Section>
        )}

      </div>
    </Modal>
  );
}

function MemberRow({
  board,
  member,
  isOwner,
  isMe,
  onBoardChange,
  onClose,
}: {
  board: BoardDetail;
  member: BoardDetail['members'][number];
  isOwner: boolean;
  isMe: boolean;
  onBoardChange: (next: BoardDetail) => void;
  onClose: () => void;
}) {
  const { navigate } = useRouter();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function leave() {
    if (!confirm('Leave this board? You will lose access.')) return;
    setBusy(true);
    setErr(null);
    try {
      await api.leaveBoard(board.id);
      onClose();
      navigate('/');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function kick() {
    if (!confirm(`Remove ${member.name} from this board?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api.removeMember(board.id, member.id);
      onBoardChange({
        ...board,
        members: board.members.filter((m) => m.id !== member.id),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function makeOwner() {
    if (
      !confirm(
        `Transfer ownership to ${member.name}? They'll be able to manage members and the plan. You'll become a regular member.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await api.transferOwnership(board.id, member.id);
      onBoardChange({
        ...board,
        role: 'member',
        members: board.members.map((m) =>
          m.id === member.id ? { ...m, role: 'owner' } : m.role === 'owner' ? { ...m, role: 'member' } : m,
        ),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="row-nudge -mx-1 flex items-center gap-2.5 rounded-lg px-1 py-1 hover:bg-ink-3/60">
      <Avatar user={member} size={26} />
      <span className="text-sm">{member.name}</span>
      <span className="truncate text-xs text-cream-3">{member.email}</span>
      {member.role === 'owner' && <span className="chip ml-auto">Owner</span>}
      <div className={`flex items-center gap-1 ${member.role === 'owner' ? '' : 'ml-auto'}`}>
        {err && <span className="text-[11px] text-rust">{err}</span>}
        {isMe ? (
          <button
            type="button"
            className="btn btn-ghost !px-2 !py-1 text-[11px] text-rust"
            onClick={leave}
            disabled={busy}
            title="Remove yourself from this board"
          >
            Leave
          </button>
        ) : (
          isOwner && (
            <>
              {member.role !== 'owner' && (
                <button
                  type="button"
                  className="btn btn-ghost !px-2 !py-1 text-[11px]"
                  onClick={makeOwner}
                  disabled={busy}
                  title="Make this person the owner"
                >
                  Make owner
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost !px-1 !py-1 text-rust"
                onClick={kick}
                disabled={busy}
                aria-label={`Remove ${member.name}`}
                title="Remove from board"
              >
                <IconTrash size={12} />
              </button>
            </>
          )
        )}
      </div>
    </li>
  );
}

function PendingInviteRow({
  boardId,
  email,
  isOwner,
}: {
  boardId: string;
  email: string;
  isOwner: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <li className="flex flex-wrap items-center gap-2 text-cream-3">
      <span className="inline-block h-[26px] w-[26px] flex-shrink-0 rounded-full border border-dashed border-cream-4" />
      <span className="min-w-0 flex-1 truncate text-sm">{email}</span>
      {/* Right cluster: chip + Resend button + error. flex-shrink-0 so
          the Resend button is never clipped off the right edge of the
          row when the email or modal is narrow. */}
      <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
        <span className="chip">Invited</span>
        {isOwner && (
          <button
            type="button"
            className="btn btn-primary !px-2.5 !py-1 text-[11px]"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await api.resendInvite(boardId, email);
                setDone(true);
                window.setTimeout(() => setDone(false), 1500);
              } catch (e) {
                setErr(e instanceof Error ? e.message : 'Failed');
              } finally {
                setBusy(false);
              }
            }}
            title="Send the invitation email again"
          >
            {busy ? 'Sending…' : done ? 'Sent ✓' : 'Resend invite'}
          </button>
        )}
      </div>
      {err && <span className="basis-full text-[11px] text-rust">{err}</span>}
    </li>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-2">
        <h3 className="text-sm font-medium">{title}</h3>
        {subtitle && <p className="text-xs text-cream-3">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function LinkRow({
  url,
  copied,
  onCopy,
  name,
}: {
  url: string;
  copied: boolean;
  onCopy: () => void;
  name: Extract<UiNodeName, 'share-board-link' | 'share-deck-link'>;
}) {
  return (
    <div className="flex items-stretch gap-2">
      <input
        className="input flex-1 font-mono text-xs"
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
      />
      <UiNode
        as="button"
        name={name}
        entity="link"
        action="copy"
        state={copied ? 'busy' : 'idle'}
        className="btn btn-ghost inline-flex items-center gap-1.5"
        onClick={onCopy}
        type="button"
      >
        <span key={copied ? 'yes' : 'no'} className={copied ? 'inline-flex items-center gap-1.5 pop-in' : 'inline-flex items-center gap-1.5'}>
          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </span>
      </UiNode>
    </div>
  );
}
